import { useEffect, useState } from 'react';
import {
  BANNERS,
  SEVERITY_ORDER,
  type BannerConfig,
  type BannerScope,
  type BannerPageContext,
  type BannerType,
} from '@/config/banners';
import { useGmailConnectionStatus } from './useGmailConnectionStatus';
import { usePermissions } from './usePermissions';
import { useBannerDismissalsStore } from '@/stores';
import { userService, settingsService, householdService } from '@/services';
import type { UserProfile, Subscription, DigestDeliveryFailure } from '@/types';
import type { ApprovedSender } from '@/services/settings.service';

export interface UseSystemBannersOptions {
  scope: BannerScope;
  pageContext?: BannerPageContext;
  householdId: string | null | undefined;
}

export interface UseSystemBannersResult {
  banners: BannerConfig[];
  isLoading: boolean;
}

// Banners that prompt the user to act on the household's Gmail,
// approved-sender, or billing configuration. These actions are
// OWNER-only (PRIMARY): Gmail, approved senders, and billing are all
// gated to my_owner_household_ids() in RLS, and their settings pages
// redirect any non-PRIMARY user. A CO_ADMIN is a manager but NOT an
// owner, so these must gate on isOwner — gating on canManageMembers
// (PRIMARY+CO_ADMIN) would show a Co-Admin a "reconnect Gmail" /
// "payment failed" prompt whose CTA bounces them right back.
const PRIMARY_ONLY_BANNERS = new Set<BannerType>([
  'gmail_watch_expired',
  'gmail_revoked',
  'gmail_not_connected',
  'gmail_expiring_soon',
  'gmail_needs_attention',
  'gmail_internal_issue',
  'approved_senders_empty',
  'billing_payment_failed',
]);

export function useSystemBanners(
  options: UseSystemBannersOptions,
): UseSystemBannersResult {
  const gmail = useGmailConnectionStatus(options.householdId);
  const { isOwner, resolved: roleResolved } = usePermissions();

  const dismissals = useBannerDismissalsStore((s) => s.dismissals);
  const dismissalsLoaded = useBannerDismissalsStore((s) => s.loaded);
  const dismissalsLoading = useBannerDismissalsStore((s) => s.loading);
  const loadDismissals = useBannerDismissalsStore((s) => s.load);

  const [profile, setProfile] = useState<UserProfile | null | undefined>(
    undefined,
  );
  const [approvedSenders, setApprovedSenders] = useState<
    ApprovedSender[] | undefined
  >(undefined);
  const [approvedSendersError, setApprovedSendersError] =
    useState<Error | null>(null);
  const [subscription, setSubscription] = useState<
    Subscription | null | undefined
  >(undefined);
  // Whether the user replied STOP (user_contact_channels.opted_out_at set).
  // Not part of `isLoading`: it only drives an informational banner, so it
  // can resolve a beat after the page renders without blocking other banners.
  const [smsOptedOut, setSmsOptedOut] = useState(false);
  // The caller's latest digest-SMS delivery, populated only when it
  // terminally failed (server-side filter in get_my_latest_digest_failure).
  // Same non-blocking treatment as smsOptedOut above.
  const [digestFailure, setDigestFailure] =
    useState<DigestDeliveryFailure | null>(null);

  useEffect(() => {
    if (!dismissalsLoaded && !dismissalsLoading) {
      void loadDismissals();
    }
  }, [dismissalsLoaded, dismissalsLoading, loadDismissals]);

  useEffect(() => {
    let cancelled = false;
    userService
      .getSmsContactChannel()
      .then((c) => {
        if (!cancelled) setSmsOptedOut(!!c?.opted_out_at);
      })
      .catch(() => {
        if (!cancelled) setSmsOptedOut(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    userService
      .getLatestDigestFailure()
      .then((f) => {
        if (!cancelled) setDigestFailure(f);
      })
      .catch(() => {
        if (!cancelled) setDigestFailure(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    userService
      .getProfile()
      .then((p) => {
        if (!cancelled) setProfile(p);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!options.householdId) {
      setApprovedSenders([]);
      setApprovedSendersError(null);
      return;
    }
    setApprovedSendersError(null);
    settingsService
      .getApprovedSenders(options.householdId)
      .then((senders) => {
        if (!cancelled) setApprovedSenders(senders);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setApprovedSenders([]);
          setApprovedSendersError(
            err instanceof Error ? err : new Error(String(err)),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [options.householdId]);

  useEffect(() => {
    let cancelled = false;
    if (!options.householdId) {
      setSubscription(null);
      return;
    }
    householdService
      .getSubscription(options.householdId)
      .then((sub) => {
        if (!cancelled) setSubscription(sub);
      })
      .catch(() => {
        if (!cancelled) setSubscription(null);
      });
    return () => {
      cancelled = true;
    };
  }, [options.householdId]);

  const isLoading =
    gmail.isLoading ||
    !dismissalsLoaded ||
    profile === undefined ||
    approvedSenders === undefined ||
    subscription === undefined;

  if (isLoading) return { banners: [], isLoading: true };

  const conditionByType: Record<BannerType, boolean> = {
    gmail_watch_expired: gmail.data?.state === 'expired',
    // gmail_revoked: distinct from `expired` (push channel timed out, can
    // be renewed) — `revoked` means Google answered a refresh with
    // invalid_grant (user pulled the OAuth grant, refresh_token aged out,
    // or the OAuth client was rotated). webhooks-gmail-watch sets
    // gmail_subscriptions.revoked_at when it sees this; the store maps
    // that to `state === 'revoked'` and we flip the banner here.
    gmail_revoked: gmail.data?.state === 'revoked',
    gmail_not_connected: gmail.data?.state === 'not_connected',
    // Stripe writes `PAST_DUE` (uppercase enum value) on
    // customer.subscription.updated when auto-renewal fails. RLS lets
    // household members read the row, but only PRIMARY can act on it —
    // PRIMARY_ONLY_BANNERS gates caregivers below.
    billing_payment_failed: subscription?.status === 'PAST_DUE',
    // Suppressed: the 7-day Gmail watch renews autonomously (daily cron at
    // the 2-day mark) and, failing that, useGmailAutoHeal silently re-arms it
    // via the stored refresh_token on app load. `expiring_soon` is therefore a
    // self-healing, non-actionable state — surfacing a "reconnect" banner for
    // it nagged users about a connection that was never actually broken. If
    // the watch genuinely lapses, `gmail_watch_expired` (critical) still
    // fires. Driven by `expiring_soon` only via useGmailAutoHeal now.
    gmail_expiring_soon: false,
    // Recoverable renewal failure surfaced by the server flag
    // watch_renewal_failed_at (store maps it to `needs_attention`). Warning
    // severity, so a simultaneous critical expired/revoked still outranks it
    // via SEVERITY_ORDER below. useGmailAutoHeal also retries the refresh, so
    // it self-clears when the next re-arm succeeds.
    gmail_needs_attention: gmail.data?.state === 'needs_attention',
    onboarding_incomplete: !profile?.onboarding_completed_at,
    // See note above: `paused` doesn't currently differentiate between
    // user-paused and system-paused. Re-enable when we have a dedicated
    // state with copy that fits.
    gmail_internal_issue: false,
    // llm_degraded: backed by a future health signal (e.g. an
    // `app_settings.llm_degraded_at` flag updated by ops or an SLO
    // dashboard). Stays false until we have that signal.
    llm_degraded: false,
    // System-managed rows (e.g. the user's own Gmail seeded by
    // gmail-forwarding-setup) don't count — they aren't a sender the
    // user picked, so the "you have no approved senders" nudge must
    // still fire even though the table has one row.
    approved_senders_empty:
      gmail.data?.state === 'connected' &&
      !approvedSendersError &&
      (approvedSenders?.filter((s) => !s.system_managed).length ?? 0) === 0,
    // Surfaced when the user has replied STOP and not yet re-enabled. Global,
    // info, forever-dismissable. Applies to any recipient (not PRIMARY-only).
    sms_paused: smsOptedOut,
    // Surfaced when the caller's own latest digest-SMS attempt terminally
    // failed. Per-recipient (each household member gets their own digest —
    // YAR-268), so not PRIMARY-only. Reappearance-on-newer-failure is
    // handled in the dismissal filter below, not here.
    digest_delivery_failed: !!digestFailure,
    // Page banner drawn by the Review v0 page from its own plan reads; never
    // part of the global stack.
    plan_limit_reached: false,
  };

  const dismissalMap = new Map<
    string,
    { snoozed_until: string | null; dismissed_at: string }
  >();
  for (const d of dismissals) {
    dismissalMap.set(d.banner_type, {
      snoozed_until: d.snoozed_until,
      dismissed_at: d.dismissed_at,
    });
  }

  const now = Date.now();
  const active = (Object.values(BANNERS) as BannerConfig[]).filter((banner) => {
    if (!conditionByType[banner.type]) return false;
    if (banner.scope !== options.scope) return false;
    if (banner.scope === 'page' && banner.pageContext !== options.pageContext) {
      return false;
    }
    // Hide owner-only banners for every non-owner (CO_ADMIN/EDITOR/
    // VIEWER). Wait until the role is resolved so we don't briefly
    // flash a banner during initial load.
    if (roleResolved && !isOwner && PRIMARY_ONLY_BANNERS.has(banner.type)) {
      return false;
    }

    const dismissal = dismissalMap.get(banner.type);
    if (!dismissal) return true;

    // digest_delivery_failed dismisses per-OCCURRENCE, not forever: a
    // dismissal only suppresses the failure that existed at dismiss time.
    // A newer failed delivery (created_at after the dismissal) still
    // surfaces the banner — otherwise a parent who dismissed last week's
    // failure would never see a fresh one. digestFailure is already the
    // single truly-latest digest attempt (see get_my_latest_digest_failure),
    // so "newer than the dismissal" is the whole comparison.
    //
    // dismissed_at is stamped by the CLIENT clock (banner-dismissal.service
    // .ts), while digestFailure.created_at is stamped by the SERVER clock
    // (notification_deliveries). A few minutes of client/server clock skew
    // is routine — without a grace window, a client clock that lags the
    // server's could make the failure the user JUST dismissed look "newer"
    // than the dismissal and reappear immediately. Only treat a failure as
    // genuinely newer once it clears plausible clock drift.
    if (banner.type === 'digest_delivery_failed') {
      if (!digestFailure) return false;
      const CLOCK_SKEW_GRACE_MS = 5 * 60 * 1000;
      return (
        new Date(digestFailure.created_at).getTime() >
        new Date(dismissal.dismissed_at).getTime() + CLOCK_SKEW_GRACE_MS
      );
    }

    if (banner.dismiss === 'forever') return false;
    if (banner.dismiss === 'snooze' && dismissal.snoozed_until) {
      return new Date(dismissal.snoozed_until).getTime() < now;
    }
    if (banner.dismiss === 'none') return true;
    return true;
  });

  active.sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
  );

  return { banners: active, isLoading: false };
}
